import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CommonServices } from '@asamco/base';
import { isNullOrWhitespace, ServiceHelperService } from '@asamco/utils';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Group } from './group.interface';

export const globalGroupScopeValue = 'Global';

export interface UserInGroupDto {
	groupId: number;
	groupName: string;
	userId: number;
	userDisplayName: string;
}

export interface UserWithGroups {
	userId: number;
	groupIds: number[];
	scopeObjectId: number;
}

/**
 *Service for groups
 *
 * @export
 */
@Injectable({
	providedIn: 'root',
	deps: [ServiceHelperService, CommonServices],
})
export class GroupService {
	/** Database actions */
	private dbActions = this.serviceHelper.createHelper<Group>('group');

	/** Get all groups */
	getAll = this.dbActions.getAll;

	/** Get group by id */
	get = this.dbActions.get;

	/** Save group */
	save = this.dbActions.save;

	/** Delete group */
	delete = this.dbActions.delete;

	/**
	 * Creates an instance of GroupService.
	 * @param serviceHelper
	 * @param cs
	 */
	constructor(private serviceHelper: ServiceHelperService, private http: HttpClient) {}

	/**
	 * Import the group json file
	 * @param file
	 */
	importFile(file: any): Observable<Group> {
		// Create form data
		const formData: FormData = new FormData();
		formData.append('file_upload', file, file.name);
		return this.http.post<Group>('group/import', formData);
	}

	getScopes(): Observable<{ value: string; caption: string }[]> {
		return this.http.get<string[]>(`group/scopes`).pipe(
			map(scopes => {
				const mappedScopes = scopes.map(s => ({
					value: s,
					caption: s,
				}));

				mappedScopes.unshift({
					value: globalGroupScopeValue,
					caption: 'Global',
				});

				return mappedScopes;
			})
		);
	}

	getUsersPerGroup(scope: string, objectId: number): Observable<UserInGroupDto[]> {
		if (isNullOrWhitespace(scope)) {
			return this.http.get<UserInGroupDto[]>(`group/UsersPerGroup`);
		} else {
			return this.http.get<UserInGroupDto[]>(`group/UsersPerGroup/${scope}/${objectId}`);
		}
	}

	getGroupsInScope(scope: string): Observable<Group[]> {
		return this.http.get<Group[]>(`group/scope/${scope}`);
	}

	addUsersToGroup(usersWithGroups: UserWithGroups[]): Observable<boolean> {
		return this.http.post<boolean>(`usergroup/add`, usersWithGroups);
	}

	removeUser(groupId: number, userId: number, scopeObjectId: number = 0) {
		if (scopeObjectId > 0) {
			return this.http.delete(`usergroup/user/${userId}/group/${groupId}/scope/${scopeObjectId}`);
		} else {
			return this.http.delete(`usergroup/user/${userId}/group/${groupId}`);
		}
	}
}
